import { translate, type Lang } from './i18n'
import type { ProfileComparison, ProfileScorecard, TargetCheck } from './api'

/**
 * Dos perfiles de estrategia, uno al lado del otro (§11.1, Figura 5).
 *
 * Es lo que le faltaba al lazo de la gobernanza: INSIGHT ya decía cómo le fue a cada perfil por
 * separado, pero no cuál funcionó mejor, que es la pregunta con la que se decide la versión
 * siguiente.
 *
 * **Y no es un experimento.** Dos perfiles nunca corrieron sobre el mismo periodo: uno fue en
 * septiembre y el otro en octubre, con otro delito y otra gente disponible. Por eso la pantalla
 * pone primero **las metas de cada uno** —lo único comparable de verdad, porque la vara la puso
 * cada perfil para sí—, después **las condiciones en que trabajó**, y sólo al final los números
 * en crudo. Coronar al que saque el número más alto sería atribuirle al perfil lo que hizo el
 * calendario.
 */
export function ProfileComparisonPanel({
  lang, comparison, onClose,
}: {
  lang: Lang
  comparison: ProfileComparison
  onClose: () => void
}) {
  const { a, b } = comparison
  const pct = (x: number | null) => (x === null ? '—' : `${Math.round(x * 100)}%`)
  const num = (x: number) => (Number.isInteger(x) ? String(x) : x.toFixed(1))
  const nombre = (s: ProfileScorecard) => `${s.profile.name} v${s.profile.version}`

  /** El cumplimiento de una meta, con su chip. Sin dato no se pinta ni bien ni mal. */
  const meta = (t: TargetCheck | undefined) => {
    if (!t) return <td className="num">—</td>
    const valor = t.target === 'maxDeferredPerShift'
      ? `${t.actual === null ? '—' : num(t.actual)} / ${num(t.goal!)}`
      : `${pct(t.actual)} / ${pct(t.goal)}`
    return (
      <td className="num">
        <span className={t.met === null ? 'chip' : t.met ? 'chip ok' : 'chip warning'}>{valor}</span>
      </td>
    )
  }

  const buscar = (s: ProfileScorecard, target: string) => s.targets.find((t) => t.target === target)
  const objetivos = [...new Set([...a.targets, ...b.targets].map((t) => t.target))]

  const fila = (clave: string, izq: string, der: string) => (
    <tr key={clave}>
      <td>{translate(lang, `compareProfiles.${clave}`)}</td>
      <td className="num">{izq}</td>
      <td className="num">{der}</td>
    </tr>
  )

  return (
    <section className="panel">
      <div className="row-line">
        <h2 className="grow">
          {translate(lang, 'compareProfiles.title', [nombre(a), nombre(b)])}
        </h2>
        <button onClick={onClose}>{translate(lang, 'compareProfiles.close')}</button>
      </div>

      {/*
        La advertencia va arriba y no al pie, y va siempre. Solo puede haber un perfil vigente a la
        vez, así que dos perfiles nunca cubren los mismos turnos: no hay caso en que sobre. Una
        primera versión la ocultaba cuando las fechas coincidían, que es justo cuando más engaña
        —dos perfiles del mismo día en turnos distintos—. Y va arriba porque quien lee una tabla de
        dos columnas ya decidió quién ganó antes de llegar al pie.
      */}
      <p className="notice warning">{translate(lang, 'compareProfiles.differentPeriods')}</p>

      <h3>{translate(lang, 'compareProfiles.ownGoals')}</h3>
      <p className="notice">{translate(lang, 'compareProfiles.ownGoalsHelp')}</p>
      {objetivos.length === 0 ? (
        <p className="notice">{translate(lang, 'compareProfiles.noGoals')}</p>
      ) : (
        <table className="compare">
          <thead>
            <tr>
              <th>{translate(lang, 'compareProfiles.goal')}</th>
              <th className="num">{nombre(a)}</th>
              <th className="num">{nombre(b)}</th>
            </tr>
          </thead>
          <tbody>
            {objetivos.map((t) => (
              <tr key={t}>
                <td>{translate(lang, `strategy.${t === 'maxDeferredPerShift' ? 'maxDeferred' : t}`)}</td>
                {meta(buscar(a, t))}
                {meta(buscar(b, t))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <table className="compare">
        <caption>{translate(lang, 'compareProfiles.conditions')}</caption>
        <tbody>
          {fila('period',
            a.outcome.from === null ? '—' : `${a.outcome.from} → ${a.outcome.to}`,
            b.outcome.from === null ? '—' : `${b.outcome.from} → ${b.outcome.to}`)}
          {fila('shifts', String(a.outcome.shifts), String(b.outcome.shifts))}
          {fila('officers', num(a.outcome.officersPerShift), num(b.outcome.officersPerShift))}
          {fila('absent', num(a.outcome.absentPerShift), num(b.outcome.absentPerShift))}
          {fila('incidents', num(a.outcome.incidentsPerShift), num(b.outcome.incidentsPerShift))}
          {fila('incidentMinutes', num(a.outcome.incidentMinutesPerShift), num(b.outcome.incidentMinutesPerShift))}
        </tbody>
      </table>

      <table className="compare">
        <caption>{translate(lang, 'compareProfiles.outcome')}</caption>
        <tbody>
          {fila('planned', String(a.outcome.planned), String(b.outcome.planned))}
          {fila('released', String(a.outcome.released), String(b.outcome.released))}
          {fila('completed', String(a.outcome.completed), String(b.outcome.completed))}
          {fila('deferred', String(a.outcome.deferred), String(b.outcome.deferred))}
          {fila('onTime', pct(a.outcome.onTimeRate), pct(b.outcome.onTimeRate))}
          {fila('presence', pct(a.outcome.presenceRate), pct(b.outcome.presenceRate))}
          {fila('late', String(a.outcome.lateArrivals), String(b.outcome.lateArrivals))}
          {fila('shortPresence', String(a.outcome.shortPresence), String(b.outcome.shortPresence))}
        </tbody>
      </table>

      <p className="notice">{translate(lang, 'compareProfiles.footnote')}</p>
    </section>
  )
}
